// @ts-strict-ignore
import Highcharts from 'highcharts';
import sortBy from 'lodash/sortBy';

import {formatDate, UNITS} from 'common/util/date';
import {TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {ANOMALY_MARKERS_ID_SUFFIX} from 'common/report/constants';
import {getChartSegmentColorCombo} from 'common/util/colors';
import * as BookmarkTypes from 'common/types/reports/bookmark';
import {isIncompleteInterval} from 'common/util/chart';

import {DisplayPlotStyle, LineStyle, TimeSeriesType} from './enums';
import {IChartDataItem, LineChartProps, SeriesData} from './types';

export function defaultXAxisLabelFormatter(value: number | Date, unit: TimeUnit): string {
  return formatDate(value, {
    unit,
    displayRangeIfWeek: false,
    customFormatting: {
      [UNITS.minute]: `h:mma`,
      [UNITS.hour]: `MMM D, ha`,
      [UNITS.day]: `MMM D`,
      [UNITS.week]: `MMM D`,
      [UNITS.month]: `MMMM`,
      [UNITS.quarter]: `[Q]Q YYYY`,
      [UNITS.year]: `YYYY`,
    },
  });
}

export function getHighchartsColorString(color: Highcharts.ColorType): string {
  return typeof color === `string` ? color : (color as Highcharts.PatternObject)?.pattern?.color;
}

function getColorWithAlpha(color: Highcharts.ColorType, alpha: number /* float in range [0, 1] */) {
  return new Highcharts.Color(color).setOpacity(alpha).get(`rgba`) as string;
}

function getLineChartType(
  dataSegment: IChartDataItem,
  {displaySeriesType, displayPlotStyle}: Pick<LineChartProps, `displaySeriesType` | `displayPlotStyle`>,
) {
  if (displaySeriesType === TimeSeriesType.Column) {
    return `column`;
  }

  let vizType = dataSegment.vizType ? dataSegment.vizType : `line`;
  const data = dataSegment.data;
  const isStacked = displayPlotStyle === DisplayPlotStyle.Stacked;
  const isAreaRange = data?.[0]?.length === 3;
  if (isAreaRange) {
    vizType = `arearange`;
  } else if (isStacked) {
    vizType = `area`;
  }
  return vizType;
}

/**
 * Converts color to a Highcharts.PatternObject of hatched diagonal lines of that color. Used for column
 * chart comparison bars.
 *
 * Highcharts accepts this object wherever it accepts a color, [docs
 * here](https://www.highcharts.com/docs/chart-design-and-style/pattern-fills)
 */
function getComparisonFill(color: string): Highcharts.PatternObject {
  return {
    // For other examples, there are 10 predefined patterns in the Highcharts.patterns array
    // (add `console.log(Highcharts.patterns)` after `HighchartsPatternFill(Highcharts)` above)
    pattern: {
      path: `M 0 5 L 5 0 M 4.5 5.5 L 5.5 4.5 M -0.5 0.5 L 0.5 -0.5`,
      width: 5,
      height: 5,
      color,
    },
  };
}

function getMarkerSeries(chartData: IChartDataItem[]) {
  const markerSeries = [];
  chartData.forEach((dataSegment) => {
    if (dataSegment.markers?.length > 0) {
      const markers = dataSegment.markers;
      const markerSerie = markers.map((marker) => {
        return marker.data;
      });
      const markerColor = markers[0].color ?? dataSegment.color;
      markerSeries.push({
        data: markerSerie,
        name: dataSegment.name,
        color: markerColor,
        id: dataSegment.name + ANOMALY_MARKERS_ID_SUFFIX,
        linkedTo: dataSegment.name,
        visible: dataSegment.visible,
        vizType: `scatter`,
      });
    }
  });
  return markerSeries;
}

export function formatLineChartSeries(
  chartData: IChartDataItem[],
  {
    displaySeriesType,
    displayPlotStyle,
    timeUnit,
    incompleteFromDate,
    incompleteToDate,
    utcOffset,
    onHighchartMouseOut,
  }: {
    displaySeriesType: TimeSeriesType;
    displayPlotStyle: DisplayPlotStyle;
    timeUnit?: TimeUnit;
    incompleteFromDate?: boolean;
    incompleteToDate?: boolean;
    utcOffset?: number;
    onHighchartMouseOut?: (highchartChart: any) => void;
  },
): SeriesData {
  const markerSeries = getMarkerSeries(chartData);
  // prettier-ignore
  const seriesData = [...chartData, ...markerSeries];
  const isColumnChart = displaySeriesType === TimeSeriesType.Column;
  return (
    seriesData
      .map((dataSegment, segmentIdx) => {
        const isAreaRange = dataSegment.data?.[0]?.length === 3;
        const colorIdx = segmentIdx % chartData.length;
        const seriesColor = dataSegment.color || getChartSegmentColorCombo(colorIdx).primary;
        const isMarkerEnabled = dataSegment.data.length === 1 || dataSegment.vizType === `scatter`;
        // column chart's time comparison bars get a hatched pattern fill
        const color = dataSegment.dashStyle && isColumnChart ? getComparisonFill(seriesColor) : seriesColor;
        return {
          name: dataSegment.name,
          custom: {prop: dataSegment.label},
          ...(dataSegment.id ? {id: dataSegment.id} : {}),
          ...(dataSegment.linkedTo ? {linkedTo: dataSegment.linkedTo} : {}),
          data: dataSegment.data,
          type: getLineChartType(dataSegment, {displaySeriesType, displayPlotStyle}),
          color,
          marker: {
            enabled: isMarkerEnabled,
          },
          yAxis: dataSegment?.axis === BookmarkTypes.AxisAssignment.Secondary ? 1 : 0,
          visible: dataSegment.visible,
          dashStyle: isColumnChart ? LineStyle.Standard : dataSegment.dashStyle || LineStyle.Standard,
          incompleteLength: dataSegment.incompleteLength,
          isComparisonItem: dataSegment.isComparisonItem,
          ...(isAreaRange ? {fillOpacity: 0.4} : {}),
        };
      })
      // TODO opslead
      .map((lineSeries) => {
        lineSeries.data = sortBy(lineSeries.data, (d) => d[0]) as
          | Array<[number, number]>
          | Array<[number, number, number]>;
        let incompletePathsAndZoning = {};
        const dataLength = lineSeries.data.length;
        const showIncompleteToDate = incompleteToDate;
        const showIncompleteFromDate = incompleteFromDate;
        const dashStyle = lineSeries.dashStyle;
        const unspecifiedIncompleteLength = lineSeries.incompleteLength === undefined;
        const incompleteLength =
          unspecifiedIncompleteLength || lineSeries.incompleteLength > dataLength - 1 ? 1 : lineSeries.incompleteLength;
        const incompleteZoneOptions = {
          // for Highcharts configuration
          dashStyle: isColumnChart ? LineStyle.Standard : LineStyle.Incomplete,
          ...(isColumnChart
            ? {color: getColorWithAlpha(lineSeries.color as string, 0.5)}
            : {fillColor: getColorWithAlpha(lineSeries.color as string, 0.5)}),
        };
        if (timeUnit && unspecifiedIncompleteLength) {
          const isIncompletePath = isIncompleteInterval(lineSeries.data, {
            unit: timeUnit,
            utcOffset,
          });
          const showIncomplete = isIncompletePath && lineSeries.data.length >= 2;

          /** for columns, we only want to define a zone that covers 1 bar across the x axis */
          const incompleteRange = isColumnChart ? 1 : 2;

          incompletePathsAndZoning = {
            isIncompletePath,
            ...(showIncomplete && {
              zoneAxis: `x`,
              zones: [{value: lineSeries.data[dataLength - incompleteRange][0]}, incompleteZoneOptions],
            }),
          };
        } else if (dataLength > 1 && incompleteLength > 0 && (showIncompleteFromDate || showIncompleteToDate)) {
          const zones = [];
          if (showIncompleteFromDate && showIncompleteToDate) {
            // Highchart has a bug with [dot, solid, dot]. So when we are changing the line style more then once,
            // we need to make sure we define both the starting and ending position for the solid part of the line.
            zones.push({value: lineSeries.data[incompleteLength][0], ...incompleteZoneOptions});
            if (incompleteLength + 1 < dataLength - incompleteLength - 1) {
              zones.push({value: lineSeries.data[incompleteLength + 1][0], dashStyle});
              zones.push({value: lineSeries.data[dataLength - incompleteLength - 1][0], dashStyle});
            } else if (incompleteLength + 1 === dataLength - incompleteLength - 1) {
              zones.push({value: lineSeries.data[incompleteLength + 1][0], dashStyle});
            }
            zones.push({value: lineSeries.data[dataLength - 1][0], ...incompleteZoneOptions});
          } else if (showIncompleteFromDate) {
            zones.push({value: lineSeries.data[incompleteLength][0], ...incompleteZoneOptions});
            if (incompleteLength < dataLength - 1) {
              zones.push({value: lineSeries.data[incompleteLength + 1][0], dashStyle});
            }
          } else {
            // Only showIncompleteToDate is true
            if (incompleteLength < dataLength - 1) {
              zones.push({value: lineSeries.data[dataLength - incompleteLength - 1][0], dashStyle});
            }
            zones.push({value: lineSeries.data[dataLength - 1][0], ...incompleteZoneOptions});
          }
          incompletePathsAndZoning = {
            isIncompletePath: true,
            zoneAxis: `x`,
            zones,
          };
        }
        return {
          ...lineSeries,
          ...incompletePathsAndZoning,
          onHighchartMouseOut,
        };
      })
  );
}
